/* ==========================================================================
   Butiken runt studion.

   Sidhuvud, varukorgslåda, brickkort och de uppladdningsytor som ligger
   utanför studion.

   Den viktigaste raden i hela filen är den som ritar kundens egen bild på
   varje brickkort i listan. Att välja storlek är annars ett abstrakt beslut
   — "36×28, är det stort?" — och abstrakta beslut skjuts upp. Ser man sin
   egen sommarstugebild på alla fem formaten blir det i stället ett smakval,
   och smakval fattas direkt.
   ========================================================================== */

import { drawTray, drawDesign, prep, demoPhoto, DEMO_KINDS } from 'db:art';
import { mountTray3D, supports3D } from 'db:tray3d';
import * as S from 'db:store';

/* EN GÅNG PER SIDA. Modulreserven (snippets/modulreserv.liquid) kör en kopia
   av de moduler som inte hunnit köras när sidan är tolkad, och läser den här
   markeringen för att veta vilka. Spärren avbryter en andra körning innan en
   enda lyssnare satts — två uppsättningar på menyknappen öppnar och stänger
   menyn i samma klick, och den går då inte att öppna alls. */
if (window.dbModulKord && window.dbModulKord['shop.js']) throw new Error('shop.js har redan körts på sidan');
(window.dbModulKord = window.dbModulKord || {})['shop.js'] = true;

const $  = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

/* ================================================ SAFE-AREA, FRUSEN =====
   env(safe-area-inset-bottom) ÄR INTE EN ENHETSKONSTANT.

   iOS animerar den mellan 0 och 34 när Safari fäller in sin nedre rad — ett
   tjugotal steg på en halv sekund — och riktningen skiljer sig mellan
   iOS-versioner och mellan Safaris två flikfältslägen. Allt som har den i en
   layout-egenskap räknas alltså om tjugo gånger mitt under en scroll. Hos
   oss växte köpradens padding, railens höjd följde med, dockan flyttade sig
   34 px UTÖVER webbläsarens egen rörelse, och sidans padding-bottom
   ändrades lika många gånger. Panelen slutade vara styv och började sträcka
   sig — och det är skillnaden mellan "adressraden försvann" och "det
   hoppar".

   Här läses talet EN gång och skrivs till --sab. CSS använder var(--sab) i
   stället för env(), och layouten blir ett fast tal.

   BOTTENVÄRDET GÅR INTE ATT LITA PÅ VID INLÄSNING: står kromet utfällt
   rapporterar iOS 0 även på en telefon som HAR hemknappsstreck. Toppens och
   sidornas inset gör inte det — de är urtaget, och det ligger still oavsett
   krom. Har enheten en hel kant har den också ett streck, och strecket är
   34 px stående och 21 liggande. Vi tar det största av det härledda golvet
   och det uppmätta värdet: ett för stort tal kostar luft, ett för litet
   lägger köpknappen under strecket.

   Orienteringsbyte är den enda händelse som får läsa om. Under scroll rörs
   ingenting — det är hela poängen.

   LIGGER I shop.js OCH INTE I studio.js: varukorgslådans fot behöver samma
   tal och finns på alla sidor. studio.js laddas bara på produktsidan. */
{
  const dd = document.documentElement;
  const prov = document.createElement('div');
  prov.style.cssText =
    'position:fixed;left:0;bottom:0;width:0;height:0;visibility:hidden;pointer-events:none;'
    + 'padding:env(safe-area-inset-top) env(safe-area-inset-right)'
    + ' env(safe-area-inset-bottom) env(safe-area-inset-left)';
  document.body.append(prov);

  const frysSafeArea = () => {
    const cs = getComputedStyle(prov);
    const px = (v) => parseFloat(v) || 0;
    const kant = Math.max(px(cs.paddingTop), px(cs.paddingLeft), px(cs.paddingRight));
    const golv = kant > 20 ? (innerWidth > innerHeight ? 21 : 34) : 0;
    dd.style.setProperty('--sab', Math.max(golv, px(cs.paddingBottom)) + 'px');
  };

  frysSafeArea();
  addEventListener('orientationchange', () => requestAnimationFrame(frysSafeArea));
}

/* ------------------------------------------------------------ toppraden -- */

/* Meddelandena rullar lodrätt: det utgående lämnar uppåt samtidigt som det
   nya kommer underifrån. Klassen `--ut` måste tas bort igen innan elementet
   används nästa varv, annars ligger det kvar ovanför raden och kommer
   aldrig tillbaka. Städningen sker när övergången är klar — inte på en
   timer, som hade kunnat glida isär från CSS:ens varaktighet. */
const items = $$('.announce__item');
const dots = $$('[data-announce-dots] i');
if (items.length > 1) {
  let i = 0;
  setInterval(() => {
    const ut = items[i];
    ut.classList.remove('announce__item--on');
    ut.classList.add('announce__item--ut');
    ut.addEventListener('transitionend', () => ut.classList.remove('announce__item--ut'), { once: true });
    dots[i]?.removeAttribute('data-on');

    i = (i + 1) % items.length;
    items[i].classList.remove('announce__item--ut');
    items[i].classList.add('announce__item--on');
    dots[i]?.setAttribute('data-on', '');
  }, 4200);
}

/* --------------------------------------------------------------- menyn -- */

const mnav = $('[data-mobile-nav]');
$$('[data-nav-toggle]').forEach((b) => b.addEventListener('click', () => {
  const open = mnav.toggleAttribute('data-open');
  document.body.classList.toggle('is-locked', open);
}));
$$('[data-mobile-nav] a').forEach((a) => a.addEventListener('click', () => {
  mnav.removeAttribute('data-open');
  document.body.classList.remove('is-locked');
}));

/* ================================================== KUNDENS EGET MOTIV === */
/* Läses in en gång och delas av alla brickor på sidan. */

let mine = null;

async function loadMine() {
  const raw = S.loadDesignRaw();
  if (!raw?.imgKey) return null;
  const got = await S.revive(raw.imgKey).catch(() => null);
  if (!got) return null;
  return { ...S.emptyDesign(), ...raw, img: got.img, imgMeta: got.meta };
}

/* Ett exempelmotiv per bricka så butiken inte står tom innan kunden laddat
   upp något. Fotona ritas i webbläsaren av art.js — se docs/03-bilder.md. */
const demoCache = new Map();
function demoFor(handle) {
  if (!demoCache.has(handle)) {
    /* Rotera efter plats i katalogen, inte efter en hash av namnet — en hash
       ger krockar och då står två grannkort med samma motiv. */
    const i = S.TRAYS.findIndex((t) => t.handle === handle);
    const cv = demoPhoto(DEMO_KINDS[(i < 0 ? demoCache.size : i) % DEMO_KINDS.length], 1200, 900);
    demoCache.set(handle, {
      ...S.emptyDesign(), img: cv,
      imgMeta: { naturalW: cv.naturalW, naturalH: cv.naturalH, name: 'exempel' },
    });
  }
  return demoCache.get(handle);
}

/* ===================================================== KOLLEKTIONSMOTIV = */
/* Ett kollektionsmotiv är en vanlig design med ett mönster i stället för en
   uppladdad bild. Samma modell, samma renderare, samma varukorg — det är
   därför färdiga motiv inte behöver en egen produkt i Shopify. */

const COLLECTIONS = window.DB?.collections || [];
const MOTIFS = COLLECTIONS.flatMap((c) =>
  c.designs.map((d) => ({ ...d, collectionHandle: c.handle, collectionTitle: c.title })));

/* Fotona som visar upp kollektionen. Skilda från motiven: ett foto är en
   bild på färdiga brickor, ett motiv är något som går att trycka. Varje
   foto bär sin färdiga URL — Liquid respektive testmiljön fyller i den. */
const PHOTOS = COLLECTIONS.flatMap((c) =>
  (c.photos || []).map((p) => ({ ...p, collectionHandle: c.handle, collectionTitle: c.title })));

const motifById = (id) => MOTIFS.find((m) => m.id === id) || null;

/* Ett motiv med bildfil laddas asynkront; ett gammalt mönstermotiv ritas
   direkt. Båda blir en vanlig design. */
async function designFromMotif(m) {
  if (!m) return null;
  if (m.artUrl) return S.designFromArtwork(m, m.artUrl).catch(() => null);
  return { ...S.emptyDesign(), pattern: m.pattern, colors: m.colors, bg: m.bg, bgSet: true };
}

/* Miniatyr till varukorgsraden. Ritas en gång och cachas. */
const thumbCache = new Map();
function motifThumb(tray, design) {
  const key = tray.handle + ':' + (design.pattern || '') + (design.bg || '');
  if (!thumbCache.has(key)) {
    const cv = document.createElement('canvas');
    const ctx = prep(cv, 220, 170);
    drawTray(ctx, { tray, design, box: { w: 220, h: 170 }, shadow: false });
    thumbCache.set(key, cv.toDataURL('image/jpeg', 0.72));
  }
  return thumbCache.get(key);
}

/* ------------------------------------------------------------ brickkort -- */

/* EN 3D-KONTEXT FÖR ALLA KORT, INTE EN PER KORT.

   Korten ritades platt medan hjälten och inspirationsbandet visade brickan i
   rymden — samma produkt, två olika saker för ögat. Vi vill visa brickorna i
   3D överallt där det går.

   Det går inte genom att höja MAX_3D. Varje mountTray3D är en egen
   WebGL-kontext med egen mesh, egen textur och egna kompilerade shaders.
   Webbläsare håller ett tjugotal totalt och STÄNGER DE ÄLDSTA utan förvarning
   när taket nås — fem kort plus hjälten plus studion hade slagit ihjäl
   varandra, och den som förlorade sin kontext var den kunden tittade på.

   Men korten behöver inte vara levande. De snurrar inte och de reagerar inte
   på musen; de behöver en bild. Alltså ritar EN dold kontext varje bricka i
   tur och ordning och stämplar in resultatet i kortets vanliga 2D-canvas.
   Antalet kort spelar då ingen roll — samma kontext ritar det tionde som det
   första, och de levande vyerna får behålla sina två platser.

   Mönstret är studio.js korrekturbild: keepBuffer så bufferten finns kvar när
   pixlarna läses, fastStorlek eftersom canvasen inte hänger i sidan och
   måtten sätts här. */

const STAMPEL_MAX = 900;

/* EN STÄMPEL PER VINKEL, inte en enda.

   Kameravinkeln sätts när kontexten monteras och tray3d har ingen väg att
   ändra den efteråt. Korten vill ha brickan rakt uppifrån; skyltningarnas
   reserv vill ha samma lutning som de levande vyerna bredvid, annars står en
   lutad och en platt bricka i samma rad och ser ut som två produkter.

   Kartan är därför nycklad på lutning. I praktiken blir det högst två —
   uppifrån och den lutande — och tillsammans med de två levande vyerna är vi
   på fyra kontexter av det tjugotal webbläsaren håller. */
const stamplar = new Map();
let stampelTrasig = false;

function stampeln(sida, pitch) {
  if (stampelTrasig) return null;
  const nyckel = pitch.toFixed(2);
  let st = stamplar.get(nyckel);
  if (!st) {
    if (!supports3D()) { stampelTrasig = true; return null; }
    const cv = document.createElement('canvas');
    cv.width = cv.height = sida;
    const g = mountTray3D(cv, {
      tray: S.TRAYS[0],
      idle: false,          // ingen snurr: en stämpel, inte en vy
      zoom: false,
      yaw: pitch > 1 ? 0 : -0.42,
      pitch,
      keepBuffer: true,     // annars är bufferten tom när pixlarna läses
      fastStorlek: true,    // canvasen hänger inte i sidan
    });
    if (!g) { stampelTrasig = true; return null; }
    st = { cv, g, tray: null, pitch: nyckel };
    stamplar.set(nyckel, st);
  }
  /* draw() läser canvas.width vid varje bild och resize() hoppar över allt
     när fastStorlek är satt — måtten går alltså att ändra mellan bilderna. */
  if (st.cv.width !== sida) { st.cv.width = st.cv.height = sida; }
  return st;
}

/* VAR BRICKAN SLUTAR I DEN KVADRATISKA RUTAN.

   fitDistance ställer kameran efter brickans HALVA DIAGONAL, för en vy som
   ska tåla att man vrider runt. Rakt uppifrån betyder det att en rektangel
   får sin diagonal inpassad och lämnar breda tomma fält på sidorna. Därför
   ritas stort och beskärs efteråt — alfakanalen säger på pixeln var brickan
   och dess skugga slutar, eftersom WebGL ritar mot genomskinligt.

   Skannas i 128×128, inte i full upplösning. En 900×900-ruta är 810 000
   pixlar att läsa per kort; nedskalningen gör drawImage på grafikkortet och
   lämnar 16 384 att gå igenom. Kanten hamnar då på närmaste sjundedels
   millimeter, vilket en marginal på en skannad pixel täcker.

   Rutan beror bara på brickans FORM och rutans storlek — inte på designen,
   för fanéret är ogenomskinligt oavsett vad som trycks på det. Därför räcker
   det att mäta en gång per form. */
const stampelRutor = new Map();

function brickansRuta(cv, nyckel) {
  if (stampelRutor.has(nyckel)) return stampelRutor.get(nyckel);
  const P = 128;
  const liten = document.createElement('canvas');
  liten.width = liten.height = P;
  const c = liten.getContext('2d');
  if (!c) return null;
  /* En WebGL-canvas lämnar aldrig ut en 2D-kontext — bilden måste ritas in
     i en egen canvas för att gå att läsa. Se samma anteckning i studio.js. */
  try { c.drawImage(cv, 0, 0, P, P); } catch { return null; }
  let d;
  try { d = c.getImageData(0, 0, P, P).data; } catch { return null; }

  let x0 = P, y0 = P, x1 = -1, y1 = -1;
  for (let y = 0; y < P; y++) {
    for (let x = 0; x < P; x++) {
      if (d[(y * P + x) * 4 + 3] > 8) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  /* Tom ruta är hur ett misslyckat WebGL-anrop ser ut. Då ska den platta
     ritningen träda in i stället för att ett tomt kort skickas vidare som om
     allt gått bra. */
  if (x1 < x0 || y1 < y0) return null;

  const k = cv.width / P;
  const ruta = {
    x: Math.max(0, (x0 - 1) * k),
    y: Math.max(0, (y0 - 1) * k),
    w: Math.min(cv.width, (x1 - x0 + 3) * k),
    h: Math.min(cv.height, (y1 - y0 + 3) * k),
  };
  stampelRutor.set(nyckel, ruta);
  return ruta;
}

/**
 * Stämplar in brickan i 3D i en vanlig 2D-kontext.
 * Returnerar false när det inte gick — då ritar anroparen platt i stället.
 */
function stampla3D(ctx, { tray, design, box, pitch = 1.45, skala = 1 }) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const sida = Math.round(Math.min(Math.max(box.w, box.h) * 1.6 * dpr, STAMPEL_MAX));
  const s = stampeln(sida, pitch);
  if (!s) return false;
  try {
    if (s.tray !== tray) { s.g.setTray(tray); s.tray = tray; }
    s.g.setDesign(design || null);
    s.g.frame();
    const ruta = brickansRuta(s.cv, `${tray.handle}:${sida}:${s.pitch}`);
    if (!ruta) return false;
    /* LITE LUFT, inte kant i kant. Beskärningen är exakt på pixeln, och en
       bricka som nuddar kortets kanter ser inklämd ut — särskilt de runda,
       som annars tar hela höjden. 6 % totalt är ungefär vad den platta
       ritningen lämnade. */
    const k = Math.min(box.w / ruta.w, box.h / ruta.h) * 0.94 * skala;
    const w = ruta.w * k, h = ruta.h * k;
    ctx.drawImage(s.cv, ruta.x, ruta.y, ruta.w, ruta.h,
      (box.w - w) / 2, (box.h - h) / 2, w, h);
    return true;
  } catch (fel) {
    /* Går kontexten förlorad mitt i — flikbyte, minnesbrist, en drivrutin som
       ger upp — ska korten inte sluta ritas. Vi går över till den platta
       vyn för resten av besöket i stället för att kasta samma fel per kort
       och per omritning. */
    console.warn('3D-stämpeln misslyckades — ritar platt i stället:', fel);
    stampelTrasig = true;
    return false;
  }
}

/** Brickan i 3D om det går, annars den platta ritningen. */
function ritaBricka(ctx, { tray, design, box, pitch, skala = 1 }) {
  if (stampla3D(ctx, { tray, design, box, pitch, skala })) return;
  drawTray(ctx, { tray, design, box, shadow: true, skala });
}

/* Brickans bredd delad med den STÖRSTA brickans — talet som gör att ett
   rutnät visar formaten i sann inbördes storlek.

   Största bredden läses ur katalogen och står inte som en siffra här: en ny
   bricka ska ändra skalan av sig själv, inte tyst göra den fel. */
function relativSkala(tray) {
  const störst = Math.max(...S.TRAYS.map((t) => t.w || 0), 0);
  return störst > 0 && tray.w ? tray.w / störst : 1;
}


function paintTrayCanvases() {
  $$('[data-tray-canvas]').forEach((cv) => {
    const tray = S.trayByHandle(cv.dataset.trayCanvas);
    if (!tray) return;
    const host = cv.parentElement;
    const w = Math.max(60, host.clientWidth - (cv.dataset.pad ? +cv.dataset.pad : 24));
    const h = cv.dataset.h ? +cv.dataset.h : Math.max(60, host.clientHeight - 24);
    const ctx = prep(cv, w, h);
    /* Bara i jämförande rutnät. En ensam bricka ska fylla sin ruta. */
    const skala = cv.hasAttribute('data-relativ') ? relativSkala(tray) : 1;

    /* Ett kollektionsmotiv slår kundens egen bild — kortet visar just det
       motivet, inte vad kunden råkar ha laddat upp. Motivet är en bildfil
       och laddas asynkront; brickan ritas tom först och fylls när bilden
       är på plats. */
    if (cv.dataset.design) {
      const m = motifById(cv.dataset.design);
      const f = cv.parentElement?.querySelector('.tray-card__foto');
      if (f) f.hidden = true;
      cv.hidden = false;
      ritaBricka(prep(cv, w, h), { tray, design: null, box: { w, h }, skala });
      if (m) designFromMotif(m).then((d) => {
        if (!d) return;
        S.sync(tray, d);
        ritaBricka(prep(cv, w, h), { tray, design: d, box: { w, h }, skala });
      });
      return;
    }

    /* HAR KUNDEN INGEN EGEN DESIGN RITAS BRICKAN TOM.

       Tidigare ritades ett påhittat mönster här — olika på varje format, för
       att rutnätet skulle se levande ut. Priset var att en förstagångsbesökare
       mötte nio brickor med mönster hon inte valt och inte kan beställa, och
       som inte finns i sortimentet. Rutnätet såg fyllt ut och visade
       ingenting sant.

       Tom bricka betyder omålad björkfanér: det man faktiskt får om man inte
       laddar upp något, och samma yta hennes egen bild kommer att ligga på.

       `mine` är kundens sparade design. Har hon börjat designa visas den —
       på alla format samtidigt, vilket är hela poängen med rutnätet. */
    const design = mine || null;

    /* INGEN EGEN DESIGN? DÅ STÅR FOTOT KVAR.

       Här ritades brickan tom på canvasen. Sedan Årys produktfoton kom
       ligger fotot i markupen (se snippets/tray-card.liquid) och canvasen
       är dold — en tom rendering är sämre än ett riktigt fotografi av
       produkten, och den kostar dessutom en ritning per kort.

       Canvasen tas fram först när det finns något att rita PÅ den. */
    const foto = cv.parentElement?.querySelector('.tray-card__foto');
    if (!design) { if (foto) return; }
    if (foto) foto.hidden = true;
    cv.hidden = false;

    if (design) S.sync(tray, design);
    ritaBricka(prep(cv, w, h), { tray, design, box: { w, h }, skala });
  });
}

/* Bilden av brickan på en orderrad.

   Egenskapen hette `Korrektur` fram till 2026-08-31 och heter `Din bricka`
   efter det. Varukorgen kan innehålla rader från båda sidorna om bytet —
   en kund som lade en bricka i varukorgen förra veckan och en till idag —
   så båda namnen måste läsas.

   Samma tabell finns i tjanst/worker/src/order.js och i kundkontoappen. */
const BILDNAMN = ['Din bricka', 'Korrektur'];

function brickbild(props) {
  for (const namn of BILDNAMN) {
    const v = props?.[namn];
    if (typeof v === 'string' && v.includes('/uploads/')) return v;
  }
  return null;
}

/* Miniatyrer på varukorgssidan. Liquid har redan satt src på de rader som
   bär bilden hos Shopify; den här fyller resten ur den lokala kopian. */
function paintLinePreviews() {
  $$('[data-line-preview]').forEach((img) => {
    if (img.src) return;
    const src = S.recallPreview(img.dataset.linePreview);
    if (src) { img.src = src; img.hidden = false; }
  });
}

/* -------------------------------------------------- kollektionsrutnäten --
   Kollektionerna ritas av webbläsaren ur window.DB.collections, inte av
   Liquid. Skälet: FLOW-motiven finns inte som produkter i Shopify än — de
   är ett mönster plus en färgpalett. Den dagen en kollektion blir riktiga
   produkter byts de två funktionerna nedan mot vanliga produktloopar i
   Liquid, och resten av sidan står kvar oförändrad.                        */

const KR = (n) => `${Math.round(n).toLocaleString('sv-SE')} kr`;

/* Formerna varvas så att ett mönster inte ser ut att bara finnas på en
   rektangel. */
const SHAPE_ROTATION = ['bricka-o38cm', 'bricka-rekt-36x28cm',
  'bricka-43x33cm', 'bricka-o31cm', 'bricka-27x20cm']
  .map((h) => S.TRAYS.find((t) => t.handle === h)).filter(Boolean);

function buildCollectionsGrid() {
  /* Finns fotografier används de. Saknas de faller mosaiken tillbaka på
     renderade motiv, så sektionen aldrig står tom — men ett riktigt foto
     säljer det ett mönster aldrig gör. */
  const källa = PHOTOS.length ? PHOTOS : MOTIFS;
  $$('[data-collections-grid]').forEach((box) => {
    const limit = +box.dataset.limit || källa.length;
    /* Namnet skrivs ut en gång per kollektion, på dess första platta. Fem
       plattor ur samma kollektion behöver inte upprepa namnet fem gånger —
       men den dag mosaiken bär två kollektioner måste båda synas. */
    let förra = null;
    box.innerHTML = källa.slice(0, limit).map((p) => {
      const nyKollektion = p.collectionHandle !== förra;
      förra = p.collectionHandle;
      return `
      <a class="collection-card" href="${collectionUrl(p.collectionHandle)}">
        ${p.url
          ? `<img src="${p.url}" alt="${p.alt || p.collectionTitle}" loading="lazy" width="900" height="679">`
          : `<canvas data-design-tile="${p.id}"></canvas>`}
        ${nyKollektion ? `<span class="collection-card__t">${p.collectionTitle}
          <span class="collection-card__s">Kollektion</span>
        </span>` : ''}
      </a>`;
    }).join('');
  });
}

function buildCollectionProducts() {
  const min = Math.min(...S.TRAYS.map((t) => t.price));
  $$('[data-collection-products]').forEach((box) => {
    const handle = box.dataset.collectionProducts;
    const list = MOTIFS.filter((m) => !handle || m.collectionHandle === handle);
    box.innerHTML = list.map((m, i) => {
      /* Formen varvas så att rutnätet visar att motiven går på allt — en
         rad rektanglar får det att se ut som ett enda format. */
      const tray = SHAPE_ROTATION[i % SHAPE_ROTATION.length];
      const href = studioUrl() + '?motiv=' + m.id;
      return `
      <div class="tray-card">
        <a class="tray-card__stage" href="${href}">
          <canvas data-tray-canvas="${tray.handle}" data-design="${m.id}" data-h="180"></canvas>
        </a>
        <span class="tray-card__body">
          <span class="tray-card__eyebrow">${m.collectionTitle}</span>
          <a class="tray-card__title" href="${href}">${m.title}</a>
          ${m.blurb ? `<span class="tray-card__blurb">${m.blurb}</span>` : ''}
          <span class="tray-card__foot">
            <span class="tray-card__price">Från ${S.prisHtml(min)}</span>
            <a class="btn btn--ghost btn--sm" href="${href}">Välj format</a>
          </span>
        </span>
      </div>`;
    }).join('');
  });
}

/* Sökvägarna skiljer sig mellan butiken och testmiljön. */
const collectionUrl = (h) => (SHOPIFY_ROUTES ? `/pages/kollektion-${h}` : `/kollektion/${h}/`);
/* ADRESSEN SKILJER SIG MELLAN BUTIKEN OCH FÖRHANDSVISNINGEN.

   Den tomma varukorgen länkade rakt till `/brickor/`, som bara finns i den
   lokala förhandsvisningen. I butiken gav knappen "Se brickorna" en
   404-sida — mitt i ett köp, för någon som just tömt varukorgen och ville
   börja om. Grannraden (Till kassan) hade redan den här växeln; den här
   blev kvar. */
const brickorUrl = () => (SHOPIFY_ROUTES ? '/collections/brickor' : '/brickor/');
const studioUrl = () => (SHOPIFY_ROUTES ? (window.DB.routes.studio || '/collections/brickor') : '/designa/');

/* Mosaikens plattor. Motivet fyller hela rutan, utan bricka runt om — det
   är mönstret som säljer, inte formatet. */
function paintDesignTiles() {
  $$('[data-design-tile]').forEach((cv) => {
    const m = motifById(cv.dataset.designTile);
    if (!m) return;
    const host = cv.parentElement;
    const w = host.clientWidth || 320, h = host.clientHeight || 200;
    const ctx = prep(cv, w, h);
    /* drawPattern räknar i brickans tryckyta. Låna storsäljarens mått och
       skala upp så att mönstret täcker rutan. */
    const tray = S.TRAYS.find((t) => t.popular) || S.TRAYS[0];
    const s2 = Math.max(w / tray.pw, h / tray.ph);
    ctx.save();
    ctx.translate(w / 2, h / 2);
    drawDesign(ctx, tray, designFromMotif(m), s2);
    ctx.restore();
  });
}

/* --------------------------------------------------------- tillfällen -- */

function paintOccasions() {
  $$('[data-occasion]').forEach((cv, i) => {
    const host = cv.parentElement;
    const w = host.clientWidth || 300, h = host.clientHeight || 190;
    const ctx = prep(cv, w, h);
    const photo = demoPhoto(DEMO_KINDS[i % DEMO_KINDS.length], 900, 700);
    const k = Math.max(w / photo.width, h / photo.height);
    ctx.drawImage(photo, (w - photo.width * k) / 2, (h - photo.height * k) / 2, photo.width * k, photo.height * k);
    const g = ctx.createLinearGradient(0, h * .3, 0, h);
    g.addColorStop(0, 'rgba(15,12,8,0)'); g.addColorStop(1, 'rgba(15,12,8,.78)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
  });
}

/* =============================================== UPPLADDNING PÅ ALLA YTOR = */
/* Vilken yta som helst kan bli en uppladdningsyta genom data-dropzone.
   Efter uppladdning går kunden vidare till studion — inte till en
   bekräftelsesida, inte till en varukorg. Nästa steg är alltid att se
   sin bild på en bricka. */

async function accept(f, goto) {
  const zone = $('[data-dropzone]');
  try {
    if (zone) zone.querySelector('.dropzone__title').textContent = 'Läser bilden…';
    const { key, meta, img } = await S.ingest(f);
    const d = { ...(S.loadDesignRaw() || S.emptyDesign()), imgKey: key, imgMeta: meta,
      pattern: null, zoom: 1, x: 0, y: 0, rot: 0, mode: 'fyll' };
    S.saveDesign(d);
    mine = { ...d, img, imgMeta: meta };
    if (goto) { window.location.href = goto; return; }
    paintTrayCanvases();
    if (zone) zone.querySelector('.dropzone__title').textContent = 'Klart — välj bricka nedan';
  } catch (err) {
    if (zone) zone.querySelector('.dropzone__title').textContent = err.message || 'Bilden gick inte att läsa';
  }
}

$$('[data-dropzone]').forEach((zone) => {
  const input = zone.querySelector('input[type=file]') || (() => {
    const i = document.createElement('input');
    i.type = 'file'; i.accept = 'image/*';
    zone.appendChild(i);
    return i;
  })();
  const goto = zone.dataset.goto || '';

  zone.addEventListener('click', (e) => { if (e.target !== input) input.click(); });
  input.addEventListener('change', () => { if (input.files?.[0]) accept(input.files[0], goto); });

  ['dragenter', 'dragover'].forEach((ev) =>
    zone.addEventListener(ev, (e) => { e.preventDefault(); zone.setAttribute('data-over', ''); }));
  ['dragleave', 'drop'].forEach((ev) =>
    zone.addEventListener(ev, (e) => { e.preventDefault(); zone.removeAttribute('data-over'); }));
  zone.addEventListener('drop', (e) => {
    const f = e.dataTransfer?.files?.[0];
    if (f) accept(f, goto);
  });
});

/* Klistra in fungerar överallt utom i studion, som har sin egen hantering. */
if (!document.querySelector('[data-studio]')) {
  window.addEventListener('paste', (e) => {
    const it = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith('image/'));
    const zone = $('[data-dropzone]');
    if (it && zone) accept(it.getAsFile(), zone.dataset.goto || '');
  });
}

/* ==================================================== VARUKORGSLÅDAN ==== */

const drawer = $('[data-drawer]');
const scrim = $('[data-scrim]');

function openCart(open = true) {
  drawer?.toggleAttribute('data-open', open);
  scrim?.toggleAttribute('data-open', open);
  document.body.classList.toggle('is-locked', open);
}

$$('[data-open-cart]').forEach((b) => b.addEventListener('click', () => openCart(true)));
$$('[data-close-cart]').forEach((b) => b.addEventListener('click', () => openCart(false)));
scrim?.addEventListener('click', () => openCart(false));
window.addEventListener('db:open-cart', () => { paintCart(); openCart(true); });
window.addEventListener('keydown', (e) => { if (e.key === 'Escape') openCart(false); });

/* Varukorgen finns i två varianter.

   I butiken äger Shopify den och vi läser /cart.js. I testmiljön finns
   ingen Shopify, och då ligger den i localStorage. Skillnaden syns bara
   här — resten av gränssnittet får samma form på datan oavsett.

   routes.cartAdd sätts bara av theme/snippets/shop-data.liquid, så den
   duger som signal för vilken värld vi är i. */
const SHOPIFY = !!window.DB?.routes?.cartAdd;
const SHOPIFY_ROUTES = SHOPIFY;

async function readLines() {
  if (!SHOPIFY) {
    const items = S.readCart();
    return { items, totals: S.cartTotals(items) };
  }
  const c = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const items = c.items.map((i) => ({
    id: i.key,
    title: i.product_title,
    dims: i.properties?.Format || '',
    dpi: parseInt(i.properties?.['Upplösning'] || '', 10) || 0,
    price: i.final_price / 100,
    /* Före radens rabatter. Skiljer sig under en kampanj — och då ska
       kunden se vad brickan annars kostar, inte bara en lägre siffra. */
    ordinarie: i.original_price / 100,
    qty: i.quantity,
    /* Per orderrad, inte per produkt: två brickor av samma format med olika
       motiv är olika rader och ska visa olika bilder.

       Bilden ligger på ORDERRADEN hos Shopify sedan brickan lades i
       varukorgen. Den lokala kopian är bara reserv — den finns inte om
       kunden bytt enhet, öppnat i ett privat fönster eller rensat sin
       lagring, och då stod raden tidigare utan bild. */
    thumb: brickbild(i.properties) || S.recallPreview(i.key),
    file: i.properties?.Motiv || null,
  }));
  const sub = c.items_subtotal_price / 100;
  const threshold = S.TERMS.freeShippingThreshold ?? Infinity;
  const free = sub >= threshold;
  return {
    items,
    totals: {
      qty: c.item_count, sub, free,
      ship: c.item_count === 0 || free ? 0 : (S.TERMS.shippingCost ?? 0),
      total: sub + (free || c.item_count === 0 ? 0 : (S.TERMS.shippingCost ?? 0)),
      toFree: Math.max(0, threshold - sub),
    },
  };
}

async function changeQty(id, qty) {
  if (!SHOPIFY) { S.setQty(id, qty); return; }
  await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, quantity: Math.max(0, qty) }),
  });
  paintCart();
}

async function paintCart() {
  const body = $('[data-cart-body]');
  const foot = $('[data-cart-foot]');
  if (!body) return;

  const { items: list, totals: t } = await readLines().catch(() => ({ items: [], totals: S.cartTotals([]) }));

  $$('[data-cart-count]').forEach((el) => {
    el.textContent = t.qty;
    el.hidden = t.qty === 0;
  });

  if (!list.length) {
    body.innerHTML = `
      <div class="empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M3 6h18l-2 12H5z"/><path d="M8 6a4 4 0 0 1 8 0"/></svg>
        <p>Varukorgen är tom.</p>
        <a class="btn btn--ghost btn--sm" href="${brickorUrl()}">Se brickorna</a>
      </div>`;
    foot.innerHTML = '';
    return;
  }

  const meter = t.free
    ? `<div class="ship-meter"><div class="ship-meter__t">Fri frakt ingår.</div>
         <div class="ship-meter__bar"><div class="ship-meter__fill" style="width:100%"></div></div></div>`
    : `<div class="ship-meter">
         <div class="ship-meter__t">${S.kr(t.toFree)} kvar till fri frakt</div>
         <div class="ship-meter__bar"><div class="ship-meter__fill" style="width:${Math.min(100, (t.sub / (S.TERMS.freeShippingThreshold || 1)) * 100)}%"></div></div>
       </div>`;

  body.innerHTML = meter + list.map((i) => `
    <div class="line">
      <span class="line__img">${i.thumb ? `<img src="${i.thumb}" alt="">` : ''}</span>
      <span class="line__t">
        <span class="line__name">${i.title}</span>
        <span class="line__meta">${i.file ? 'Din bild bifogad' : 'Ditt motiv'} · ${i.dims}${i.dpi ? ` · ${i.dpi} dpi` : ''}</span>
        <span class="line__row">
          <span class="qty">
            <button data-step="-1" data-id="${i.id}" aria-label="Färre">−</button>
            <input value="${i.qty}" data-qty-id="${i.id}" inputmode="numeric">
            <button data-step="1" data-id="${i.id}" aria-label="Fler">+</button>
          </span>
          <b>${i.ordinarie > i.price ? `<s class="pris__ord">${S.kr(i.ordinarie * i.qty)}</s> ` : ''}${S.kr(i.price * i.qty)}</b>
        </span>
        <button class="line__x" data-remove="${i.id}">Ta bort</button>
      </span>
    </div>`).join('');

  /* ENHETEN KOMMER UR TERMS, INTE UR DEN HÄR RADEN. Här stod "arbetsdagar"
     skrivet för hand, medan toppraden, produktsidan och fraktsidan sade
     "14 dagar" — samma tal, två olika löften, och lådan är det sista kunden
     läser före kassan. productionUnit skickas av shop-data.liquid, och
     npm run validate fäller varje annat dagord som skrivs direkt efter
     talet. */
  foot.innerHTML = `
    <div class="sum"><span>Delsumma</span><span>${S.kr(t.sub)}</span></div>
    <div class="sum"><span>Frakt</span><span>${t.ship ? S.kr(t.ship) : 'Fri'}</span></div>
    <div class="sum sum--total"><span>Att betala</span><span>${S.kr(t.total)}</span></div>
    <a class="btn btn--primary btn--block" href="${SHOPIFY ? '/checkout' : '/kassa/'}" style="margin-top:14px">Till kassan</a>
    <p class="tiny muted" style="margin:10px 0 0;text-align:center">
      Du ser din bricka i varukorgen · tillverkning ${S.TERMS.productionDays} ${S.TERMS.productionUnit}
    </p>`;

  body.querySelectorAll('[data-step]').forEach((b) => b.onclick = () => {
    const cur = list.find((x) => x.id === b.dataset.id);
    changeQty(b.dataset.id, cur.qty + +b.dataset.step);
  });
  body.querySelectorAll('[data-qty-id]').forEach((el) => el.onchange = () => {
    changeQty(el.dataset.qtyId, Math.max(1, +el.value || 1));
  });
  body.querySelectorAll('[data-remove]').forEach((b) => b.onclick = () => changeQty(b.dataset.remove, 0));
}

window.addEventListener('db:cart', paintCart);

/* ========================================================== SKYLTNINGEN == */
/* Panelernas stora bricka. I Figma stod det "GIF — rörligt bildspel" på tre
   ställen; det här är den ytan. Ingen filmfil behövs: brickan ritas av
   samma renderare som studion, snurrar långsamt och byter motiv. Skarp i
   alla storlekar, och motiven kommer ur katalogen i stället för ur en
   videoredigerare.

   Den första skyltningen på sidan får 3D. Övriga ritas platt — flera
   WebGL-ytor på samma sida kostar mer än de ger. */

const SHOW_ORDER = ['bricka-o38cm', 'bricka-rekt-36x28cm', 'bricka-43x33cm'];

/* Skyltningens motiv — riktiga foton, inte ritade mönster.

   Ett abstrakt mönster på skyltningen läser kunden som "det här mönstret
   köper jag". Brickan säljs på motsatsen: att man lägger sin EGEN bild på
   den. Bröllopsfotot, hunden, studenten.

   En rad utan url hoppas över — asset_url ger tom sträng för en fil som inte
   ligger i temat än. Är listan tom faller skyltningen tillbaka på de ritade
   mönstren, så sidan aldrig står tom medan bilderna saknas. */
const SKYLTMOTIV = (window.DB?.skyltmotiv || []).filter((m) => m.url);

/* Bilderna laddas en gång och delas av alla skyltningar på sidan. En
   misslyckad laddning tas ur listan i stället för att ge en tom bricka. */
const skyltBilder = new Map();
async function laddaSkyltmotiv() {
  await Promise.all(SKYLTMOTIV.map(async (m) => {
    try { skyltBilder.set(m.fil, await S.loadArtwork(m.url)); }
    catch { /* filen finns inte — motivet hoppas över */ }
  }));
  return [...skyltBilder.keys()];
}

/* Ett motiv som en design. Samma väg som ett kollektionsmotiv tar: bilden
   fyller brickan och sync() räknar om skalan per format. */
function skyltDesign(fil) {
  const img = skyltBilder.get(fil);
  if (!img) return null;
  return {
    ...S.emptyDesign(),
    img,
    imgMeta: { naturalW: img.naturalWidth, naturalH: img.naturalHeight, name: fil },
    mode: 'fyll',
  };
}
/* HUR MÅNGA 3D-VYER SIDAN FÅR HA.

   Här stod `used3d = false` och ett villkor som gav den FÖRSTA skyltningen
   på sidan 3D och alla andra den platta ritningen. På startsidan betydde det
   att hjälten fick brickan i rymden medan inspirationsbandet längst ner visade
   en platt bild — samma sektion, två olika produkter för ögat.

   Taket finns kvar, för varje vy är en egen WebGL-kontext med egen mesh och
   egen textur, och webbläsare stänger de äldsta när de tar slut. Två räcker
   till startsidan och lämnar marginal.

   Alla vyer stoppas av sin IntersectionObserver när de rullar ur bild, så det
   är aldrig mer än en som ritar. */
const MAX_3D = 2;
let show3d = null;
const show3ds = [];

/* ======================================== BAKSIDANS STANDARDTRYCK ========
   Varje bricka bär vårt märke på undersidan. Skyltningarna visade det inte.

   mountTray3D har två texturer — `design` och `bakDesign` — och studion
   matar båda via setDesign(fram, bak). Skyltningarna skickade bara framsidan,
   och målaSida() returnerar 0 direkt när sidan saknar motiv. Undersidan fick
   alltså ingen textur alls: hjälten och inspirationsbandet går att vrida på,
   och den som vred fick se en blank bricka.

   Bilden laddas per bricka (baksida-<handle>.jpg) och delas mellan alla
   skyltningar på sidan — hjälten och inspirationsbandet visar samma fem
   format och ska inte hämta samma fil två gånger.

   FORMEN ÄR STUDIONS sidoDesign('bak'), ordagrant. Två ritningar av samma
   sida som skiljer sig åt är en bugg som väntar: `bg: 'transparent'` låter
   fanéret synas igenom (null hade gett en gräddfärgad platta), och
   `baksida: true` klipper kundens innehåll till designytan — här finns
   inget sådant, men formen ska vara densamma den dag skyltningen visar en
   bricka med tryck på baksidan.

   Guiderna följer INTE med: de ritas av drawTray och bara när `bleed` är
   satt, och texturen går genom drawDesign. */
const baksidesLöfte = new Map();   // url -> Promise
const baksidesKlar = new Map();    // url -> Image eller null

function laddaBaksida(url) {
  if (baksidesLöfte.has(url)) return baksidesLöfte.get(url);
  const p = new Promise((klar) => {
    const bild = new Image();
    bild.crossOrigin = 'anonymous';
    bild.onload = () => { baksidesKlar.set(url, bild); klar(bild); };
    bild.onerror = () => {
      baksidesKlar.set(url, null);
      console.warn('skyltning: baksidans standardtryck kunde inte laddas —', url);
      klar(null);
    };
    bild.src = url;
  });
  baksidesLöfte.set(url, p);
  return p;
}

function baksidesDesign(tray) {
  const bild = tray?.baksidaUrl ? baksidesKlar.get(tray.baksidaUrl) : null;
  if (!bild) return null;
  return {
    trayHandle: tray.handle,
    bg: 'transparent', pattern: null, img: null, imgMeta: null,
    images: [], texts: [],
    standard: { img: bild },
    baksida: true,
  };
}

function mountShowcase(cv) {
  const only = cv.dataset.collection;
  const motifs = only ? MOTIFS.filter((m) => m.collectionHandle === only) : MOTIFS;

  /* Utan bunden kollektion visar vi produkten som den används: kundens egen
     bild om det finns en, annars skyltningsmotiven.

     Har kunden en egen design vinner den ALLTID. Att visa våra exempel
     ovanpå hennes eget arbete vore att ta över sidan från henne. */
  /* LAT LISTA, inte en fast.

     Bilderna laddas över nätet och är sällan klara när skyltningen monteras.
     Räknades listan ut här hade den fastnat på reserven för alltid, och
     fotona aldrig visats. Nu frågas den om vid varje växling, så de dyker
     upp av sig själva så fort de kommit fram. */
  const looks = () => {
    if (motifs.length && only) return motifs.map((m) => ({ design: () => designFromMotif(m) }));
    const laddade = [...skyltBilder.keys()];
    if (laddade.length) return laddade.map((fil) => ({ design: () => mine || skyltDesign(fil) }));
    /* Reserv: de ritade mönstren, tills bilderna ligger i temat. */
    return SHOW_ORDER.map((h, i) => ({ design: () => mine || demoFor('skylt' + i) }));
  };

  const trays = only ? [S.trayByHandle('bricka-o38cm')] : SHOW_ORDER.map(S.trayByHandle);
  let i = 0;

  const pick = () => {
    const tray = trays[i % trays.length];
    const val = looks();
    const design = val[i % val.length].design();
    S.sync(tray, design);
    return { tray, design };
  };

  const wantsH = cv.dataset.h ? +cv.dataset.h : 320;

  /* VYN RAKT UPPIFRÅN. Sätts med data-vy="ovanifran" på canvasen.

     1.45 radianer, inte π/2. Exakt rakt ovanifrån ställer kameran i linje med
     uppvektorn och lookAt kollapsar — därför klampar tray3d.js till ±1.45,
     vilket är 83 grader och i praktiken ovanifrån. Skuggan är kvar: uFade
     tonar bort den när man tittar från SIDAN, inte uppifrån.

     Ur den vinkeln är brickan sin egen fotavtrycksform, inte en projektion.
     Rutans proportion sätts därför efter brickorna som visas, inte efter
     7:5 som mättes i den lutande vyn. */
  const uppifran = cv.dataset.vy === 'ovanifran';
  let live = null;

  /* EN VÄG SOM SÄTTER BÅDA SIDORNA.

     setDesign(fram, bak) behåller den gamla baksidan när `bak` är null
     (`bak ?? bakDesign`), så en enarmad setDesign lämnar föregående brickas
     tryck kvar. Det syns knappt — märket är detsamma på alla format — men
     det är fel bild, och den här funktionen gör att ingen behöver komma ihåg
     det.

     `visadTray` finns för att bilden laddas över nätet: byter skyltningen
     bricka medan filen är på väg ska svaret inte måla en bricka som inte
     visas längre. */
  let visadTray = null;

  const visa = (tray, design) => {
    if (!live) return;
    visadTray = tray;
    live.setTray(tray);
    live.setDesign(design, baksidesDesign(tray));
    const url = tray?.baksidaUrl;
    if (!url || baksidesKlar.has(url)) return;
    laddaBaksida(url).then(() => {
      if (live && visadTray === tray) live.setDesign(design, baksidesDesign(tray));
    });
  };

  const paint2d = () => {
    const { tray, design } = pick();
    const host = cv.parentElement;
    const w = Math.min(host.clientWidth || 420, 520);
    const h = Math.round(Math.min(wantsH, w * .82));
    const ctx = prep(cv, w, h);
    /* Reserven är inte platt av princip — den är reserv. Går 3D att rita
       stämplas brickan in med SAMMA lutning som en levande vy hade haft,
       annars står en lutad och en platt bricka i samma rad. */
    ritaBricka(ctx, { tray, design, box: { w, h }, pitch: uppifran ? 1.45 : 0.72 });
  };

  if (show3ds.length < MAX_3D && supports3D()) {
    const { tray, design } = pick();

    /* HÖJDEN FÖLJER BREDDEN, DEN STÅR INTE FAST.

       Kameran passar in brickan efter det TRÅNGASTE måttet. På en smal
       skärm är det bredden, och då blir all höjd över brickans egen
       proportion bara tom yta. Uppmätt på 375 px: canvasen var 287x340
       medan brickan tog 262x166 — 90 px tomt över och 85 under, mitt i
       hjälten, som därför blev nästan 1000 px hög.

       3:2 är brickans projicerade form, uppmätt i två vyer: 488x324 på
       skrivbord (1.506) och 262x166 på mobil (1.578). Talet varierar en
       gnutta med brickans form, så vi tar det lägsta — en canvas som är
       någon procent för hög kostar lite tom yta, medan en för låg gör
       brickan mindre.

       Ingen risk för avhuggna kanter: kameran passar alltid in brickan i
       den canvas den får. Blir rutan för trång ritas brickan mindre, den
       klipps inte.

       Som aspect-ratio i stället för en uträknad höjd, för då räknar
       webbläsaren om den vid varje omritning — telefonen som vrids,
       fönstret som dras. data-h blir taket.

       3:2 ÄR UPPMÄTT, INTE UTRÄKNAT

       Jag försökte först räkna fram formen ur kamerans egna tal. Det blev
       fel: uttrycket innehåller en påhittad marginal (rim x 1.6) som är
       större än brickans verkliga höjd, så svaret blev 1.18 och rutan växte
       i stället för att krympa.

       Mätt i webbläsaren i stället, över de brickor skyltningen växlar
       mellan: 1.578 för den trängsta och 1.744 för den bredaste. 1.5 ligger
       strax under den trängsta och räcker därför åt alla. Formen står still
       mellan bytena — räknades den om per bricka skulle hjälten hoppa i höjd
       var femte sekund.

       FÖRE monteringen, inte efter. Renderaren mäter rutan när den startar
       och sätter sin buffert efter den. Sattes stilen efteråt hade bufferten
       stannat på den gamla höjden: uppmätt 574x680 bakom en ruta som blivit
       287x191, alltså en bricka ritad i fel proportion. */
    /* 7:5 OCH INTE 3:2 — RUTAN VAR FÖR LÅG.

       3:2 sattes efter brickans UPPMÄTTA projicerade form (1.506 till 1.744)
       och räckte inte: brickan ritades utanför rutan och kapades rakt av,
       nederkanten på en bricka, högerkanten på en annan. Formen var alltså
       rätt mätt men lämnade ingen marginal, och kameran nedan hade bara 4 %.

       Två tal måste ändras tillsammans. Höjs bara marginalen krymper brickan
       i en oförändrad ruta; görs bara rutan högre ritar kameran ändå brickan
       för stor och kapningen flyttar sig. 7:5 (1.4) ger rutan ~7 % mer höjd
       vid samma bredd, precis det som fyllnaden nedan tar tillbaka — brickan
       blir alltså lika stor som förut, men hel. */
    cv.style.width = '100%';
    cv.style.height = 'auto';
    cv.style.aspectRatio = uppifran ? '6 / 5' : '7 / 5';
    cv.style.maxHeight = wantsH + 'px';

    /* Dekorativ yta: ingen zoom, och snurren återupptas när besökaren
       släppt. Se tray3d.js om varför zoom är avstängd här. */
    live = mountTray3D(cv, {
      tray, design, bakDesign: baksidesDesign(tray),
      idle: true, zoom: false, resumeIdle: true,
      /* RAKT betyder rakt. Standardvyn står på yaw -0.42 och lägger till en
         gungning på ±0.20 radianer — lutande syns det som att brickan vänder
         sig långsamt, men UPPIFRÅN blir samma rörelse en vridning i planet,
         och brickan låg snett på skärmen. Här står den axelrätt och stilla.

         Rityckan står inte still för det: loopen i tray3d.js ritar varje bild
         oavsett `idle`, som bara styr gungningen. Motivbytet var 5,6:e sekund
         syns alltså som vanligt. Att dra i brickan går fortfarande. */
      ...(uppifran ? { pitch: 1.45, yaw: 0, idle: false } : {}),
      /* INGEN EGEN MARGINAL LÄNGRE.

         Här stod först 1.04 och sedan 1.12. Båda försökte kompensera för att
         fitDistance i tray3d.js räknade ortografiskt och missade att kanten
         som svänger fram mot kameran ligger 145–195 mm närmare och ritas
         37 % större. Ett fast tal kan inte täcka det: felet varierar med
         lutningen, vilket är varför brickan kapades i vissa vinklar och inte
         i andra. Formeln är rättad — se fitDistance — och skyltningen
         använder därför samma marginal som studion. */
    });
    if (live) {
      show3ds.push(live);
      show3d = live;
      new IntersectionObserver((entries) => {
        for (const e of entries) e.isIntersecting ? live.start() : live.stop();
      }, { threshold: 0.05 }).observe(cv);
      visa(tray, design);
    }
  }
  if (!live) {
    /* 3D gick inte att starta. Rutan vi satte inför monteringen ska bort
       igen — den platta ritningen räknar ut sina egna mått, och en kvarglömd
       aspect-ratio hade slagits mot dem. */
    cv.style.width = cv.style.height = cv.style.aspectRatio = cv.style.maxHeight = '';
    paint2d();
  }

  setInterval(() => {
    i++;
    if (live) {
      const { tray, design } = pick();
      visa(tray, design);
    } else {
      paint2d();
    }
  }, live ? 5600 : 4000);

  return { live, repaint: () => { if (!live) paint2d(); } };
}

/* Kollektionens sidhuvud visar ett fotografi när det finns ett — ett riktigt
   foto av produkten slår en rendering. Saknas fotot monteras den snurrande
   brickan som vanligt. */
$$('[data-showcase][data-collection]').forEach((cv) => {
  const c = COLLECTIONS.find((x) => x.handle === cv.dataset.collection);
  if (!c?.heroUrl) return;
  const img = new Image();
  img.src = c.heroUrl;
  img.alt = `Brickor ur kollektionen ${c.title}`;
  img.loading = 'eager';
  img.className = 'slab__photo';
  cv.replaceWith(img);
});

/* Hämta hem skyltningens motiv. Skyltningarna monteras direkt och visar
   reserven under tiden; listan är lat, så fotona tar över när de kommit. */
laddaSkyltmotiv();

const showcases = $$('[data-showcase]').map(mountShowcase);

/* Snabbköp är medvetet borttaget.

   Ett motiv går på fem format, och några av dem kan falla under
   upplösningsgränsen. En knapp som lägger motivet på storsäljaren skulle
   både gissa fel format och gå förbi kvalitetsspärren. Kunden får välja
   bricka i studion i stället — där syns priset och upplösningen direkt. */

/* ---------------------------------------------------------------- start -- */

/* KAMPANJPRISER SOM LIQUID RITADE kan vara cachade från före kampanjens
   början eller efter dess slut — Shopify cachar sidan, och Liquid räknar
   dagen när sidan renderades, inte när den visas. Ritas om bara när sidan och
   webbläsaren inte är överens, så att en vanlig sidladdning inte byter ut
   samma text och flyttar raden. Se snippets/pris.liquid. */
function stämKampanjpriser() {
  $$('[data-pris]').forEach((el) => {
    const pris = Number(el.dataset.pris) / 100;
    if (!(pris > 0)) return;
    const pågår = S.kampanjPris(pris) < pris;
    if (pågår !== el.classList.contains('pris--kampanj')) el.outerHTML = S.prisHtml(pris);
  });
  /* Kampanjmärket i hjälten, av samma skäl. */
  const pågår = S.kampanjProcent() > 0;
  $$('[data-kampanjmarke]').forEach((el) => { el.hidden = !pågår; });
}

(async () => {
  stämKampanjpriser();
  mine = await loadMine();
  if (mine) {
    $$('[data-mine-only]').forEach((el) => el.hidden = false);
    $$('[data-no-mine]').forEach((el) => el.hidden = true);
  }
  buildCollectionsGrid();
  buildCollectionProducts();
  paintTrayCanvases();
  paintLinePreviews();
  paintDesignTiles();
  paintOccasions();
  paintCart();
})();

/* EN REN HÖJDÄNDRING ÄR WEBBLÄSARENS ADRESSRAD — INTE EN OMRITNING VÄRD.

   iOS ändrar innerHeight med 40–108 px varje gång kromet fälls in eller ut,
   och skickar resize flera gånger under animationen. Debouncen samlar ihop
   dem, men skuren kostar sex canvas-omallokeringar och sex WebGL-bildrutor
   på huvudtråden — mitt i den scroll som utlöste kromrörelsen. Produktsidan
   har sex sådana canvasar: en i produktstödet och fem i brickrutnätet.
   Tappade bildrutor mitt i en pågående rörelse läses som att sidan hoppar,
   och det här är den enda av våra felkällor som är på i ALLA webbläsare.

   Ingenting nedanför mäter höjden på mobil: brickornas canvasar har fast
   data-h och skalas på host.clientWidth. Skyltningarna läser clientHeight
   och kan behöva en lodrät ändring — men bara på skrivbordet, där kromet
   inte fälls in. Därför portas höjdgrenen på 900 px. */
let rt, sistBredd = innerWidth, sistHöjd = innerHeight;
const bredSkarm = matchMedia('(min-width: 900px)');
window.addEventListener('resize', () => {
  const bytBredd = innerWidth !== sistBredd;
  const bytHöjd = innerHeight !== sistHöjd;
  sistBredd = innerWidth; sistHöjd = innerHeight;
  if (!bytBredd && !(bytHöjd && bredSkarm.matches)) return;
  clearTimeout(rt);
  rt = setTimeout(() => {
    paintTrayCanvases();
    paintDesignTiles();
    paintOccasions();
    showcases.forEach((s2) => s2.repaint());
  }, 180);
});

window.addEventListener('db:design', async () => {
  mine = await loadMine();
  paintTrayCanvases();
  /* Alla vyer, inte bara den sista monterade. `show3d` pekade på den ena som
     fanns; med två blev inspirationsbandet kvar på skyltmotivet medan hjälten
     bytte till kundens egen design. */
  if (mine) {
    for (const v of show3ds) { S.sync(v.tray, mine); v.setDesign(mine); }
  }
});
